import { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, Building2, ChevronDown } from '../../icons/wp-icons';

function AnfrageForm({ anfrage, onSubmit, onCancel, isLoading, objekte }) {
  const [formData, setFormData] = useState({
    betreff: '',
    nachricht: '',
    kategorie: 'Info',
    status: 'neu',
    objektId: ''
  });
  const [errors, setErrors] = useState({});
  const [objektSearch, setObjektSearch] = useState('');
  const [objektDropdownOpen, setObjektDropdownOpen] = useState(false);
  const objektDropdownRef = useRef(null);

  useEffect(() => {
    if (anfrage) {
      setFormData({
        betreff: anfrage.betreff || '',
        nachricht: anfrage.nachricht || '',
        kategorie: anfrage.kategorie || 'Info',
        status: anfrage.status || 'neu',
        objektId: anfrage.objektId || ''
      });
    }
  }, [anfrage]);

  // Close objekt dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (objektDropdownRef.current && !objektDropdownRef.current.contains(e.target)) {
        setObjektDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredObjekte = useMemo(() => {
    if (!objekte) return [];
    if (!objektSearch.trim()) return objekte;
    const term = objektSearch.toLowerCase();
    return objekte.filter(obj => {
      const label = `${obj.name || ''} ${obj.strasse || ''} ${obj.ort || ''}`.toLowerCase();
      return label.includes(term);
    });
  }, [objekte, objektSearch]);

  const selectedObjekt = objekte?.find(o => o.id === formData.objektId);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.betreff.trim()) {
      newErrors.betreff = 'Betreff ist erforderlich';
    }

    if (!formData.nachricht.trim()) {
      newErrors.nachricht = 'Nachricht ist erforderlich';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const kategorien = ['Termin', 'Angebot', 'Info', 'Rückruf'];

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Betreff */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Betreff <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          name="betreff"
          value={formData.betreff}
          onChange={handleChange}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.betreff ? 'border-danger' : 'border-border'
          }`}
          placeholder="z.B. Kostenvoranschlag Fensterscheibe"
          disabled={isLoading}
        />
        {errors.betreff && (
          <p className="text-danger text-sm mt-1">{errors.betreff}</p>
        )}
      </div>

      {/* Kategorie */}
      <div>
        <label className="block text-sm font-medium mb-2">Kategorie</label>
        <div className="flex gap-2 flex-wrap">
          {kategorien.map((kat) => (
            <button
              key={kat}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, kategorie: kat }))}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                formData.kategorie === kat
                  ? 'bg-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              disabled={isLoading}
            >
              {kat}
            </button>
          ))}
        </div>
      </div>

      {/* Objekt-Auswahl (nur bei Hausverwaltungen) */}
      {objekte && objekte.length > 0 && (
        <div ref={objektDropdownRef} className="relative">
          <label className="block text-sm font-medium mb-2">Objekt</label>
          <button
            type="button"
            onClick={() => { if (!isLoading) { setObjektDropdownOpen(!objektDropdownOpen); setObjektSearch(''); } }}
            className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white text-left flex items-center justify-between gap-2 disabled:opacity-50"
            disabled={isLoading}
          >
            {selectedObjekt ? (
              <span className="flex items-center gap-2 truncate">
                <Building2 className="w-4 h-4 text-purple-500 shrink-0" />
                <span className="truncate">{selectedObjekt.name || selectedObjekt.strasse}{selectedObjekt.ort ? `, ${selectedObjekt.ort}` : ''}</span>
              </span>
            ) : (
              <span className="text-text-muted">Kein Objekt zugeordnet</span>
            )}
            <div className="flex items-center gap-1 shrink-0">
              {selectedObjekt && (
                <span
                  role="button"
                  onClick={(e) => { e.stopPropagation(); setFormData(prev => ({ ...prev, objektId: '' })); setObjektDropdownOpen(false); }}
                  className="p-0.5 hover:bg-gray-100 rounded"
                >
                  <X className="w-3.5 h-3.5 text-text-muted" />
                </span>
              )}
              <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${objektDropdownOpen ? 'rotate-180' : ''}`} />
            </div>
          </button>

          {objektDropdownOpen && (
            <div className="absolute z-50 mt-1 w-full bg-white border border-border rounded-lg shadow-lg overflow-hidden">
              {/* Suchfeld */}
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
                  <input
                    type="text"
                    value={objektSearch}
                    onChange={(e) => setObjektSearch(e.target.value)}
                    placeholder="Objekt suchen..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    autoFocus
                  />
                </div>
              </div>

              {/* Optionen */}
              <div className="max-h-48 overflow-y-auto">
                <button
                  type="button"
                  onClick={() => { setFormData(prev => ({ ...prev, objektId: '' })); setObjektDropdownOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition ${!formData.objektId ? 'bg-primary-light text-primary font-medium' : 'text-text-muted'}`}
                >
                  Kein Objekt zugeordnet
                </button>
                {filteredObjekte.map(obj => (
                  <button
                    type="button"
                    key={obj.id}
                    onClick={() => { setFormData(prev => ({ ...prev, objektId: obj.id })); setObjektDropdownOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition flex items-center gap-2 ${formData.objektId === obj.id ? 'bg-purple-50 text-purple-700 font-medium' : ''}`}
                  >
                    <Building2 className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                    <span className="truncate">{obj.name || obj.strasse}{obj.ort ? `, ${obj.ort}` : ''}</span>
                  </button>
                ))}
                {filteredObjekte.length === 0 && objektSearch && (
                  <p className="px-4 py-3 text-sm text-text-muted text-center">Kein Objekt gefunden</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nachricht */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Nachricht <span className="text-danger">*</span>
        </label>
        <textarea
          name="nachricht"
          value={formData.nachricht}
          onChange={handleChange}
          rows="5"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.nachricht ? 'border-danger' : 'border-border'
          }`}
          placeholder="Beschreiben Sie die Anfrage..."
          disabled={isLoading}
        />
        {errors.nachricht && (
          <p className="text-danger text-sm mt-1">{errors.nachricht}</p>
        )}
      </div>

      {/* Status (nur bei Bearbeitung) */}
      {anfrage && (
        <div>
          <label className="block text-sm font-medium mb-2">Status</label>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, status: 'neu' }))}
              className={`px-3 py-1.5 text-sm rounded-full font-medium transition ${
                formData.status === 'neu'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
              disabled={isLoading}
            >
              Neu
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, status: 'in_bearbeitung' }))}
              className={`px-3 py-1.5 text-sm rounded-full font-medium transition ${
                formData.status === 'in_bearbeitung'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
              }`}
              disabled={isLoading}
            >
              In Bearbeitung
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, status: 'abgeschlossen' }))}
              className={`px-3 py-1.5 text-sm rounded-full font-medium transition ${
                formData.status === 'abgeschlossen'
                  ? 'bg-green-600 text-white'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
              disabled={isLoading}
            >
              Abgeschlossen
            </button>
          </div>
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Wird gespeichert...' : anfrage ? 'Aktualisieren' : 'Erstellen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
          disabled={isLoading}
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export default AnfrageForm;
